import {defineStore, acceptHMRUpdate} from "pinia";
import api from "../services/api.js";
import {useUserStore} from "./users.js";

let latestTicketsRequest = 0

export const useTicketStore = defineStore("tickets", {
    state: () => ({
        _tickets: null,
        _ticket: null,
        _order_history: null,
        _filter_data: null,
        _stats: null,
        _counts: null,
        _last_params: {},
    }),

    getters: {
        tickets: (state) => state._tickets,
        ticket: (state) => state._ticket,
        order_history: (state) => state._order_history,
        filter_data: (state) => state._filter_data,
        stats: (state) => state._stats,
        counts: (state) => state._counts,
    },

    actions: {
        async getTickets(params) {
            this._last_params = params
            const requestId = ++latestTicketsRequest
            const response = await api.get('tickets', params)

            if (requestId === latestTicketsRequest) {
                this._tickets = response.data
            }
        },

        async refreshTickets() {
            await this.getTickets(this._last_params)
            await this.getCounts()
        },

        async getTicket(ticketId) {
            const response = await api.get(`tickets/${ticketId}`)
            this._ticket = response.data.ticket
            this._order_history = response.data.order_history
        },

        async getCounts() {
            const response = await api.get('tickets/counts')
            this._counts = response.data
        },

        async assignTicket(ticketId, userId) {
            await api.post(
                `tickets/${ticketId}/assign`,
                {assignee_id: userId}
            )

            await this.getTicket(ticketId)
        },

        async bulkAssignTickets(ticketIds, userId) {
            const response = await api.post(`tickets/bulk-assign`, {
                ticket_ids: ticketIds,
                assignee_id: userId
            })

            return response.data
        },

        async markAwaitingExternal(ticketId) {
            await api.post(
                `tickets/${ticketId}/awaiting-external`
            )

            await this.getTicket(ticketId)
        },

        async completeTicket(ticketId) {
            await api.post(
                `tickets/${ticketId}/complete`
            )

            await this.getTicket(ticketId)
        },

        async bulkCompleteTickets(ticketIds) {
            const response = await api.post(`tickets/bulk-complete`, {
                ticket_ids: ticketIds
            })

            return response.data
        },

        async prepareFilterData() {
            const userStore = useUserStore();

            await userStore.getAllUsers({
                all: false,
                role: 'internal-agent'
            });

            let options = [];

            userStore.users.forEach(u => {
                options.push({
                    label: `${u.first_name} ${u.last_name}`,
                    value: u.id
                })
            })

            this._filter_data = {
                sorting: [
                    {label: 'Latest first', value: 'desc'},
                    {label: 'Oldest first', value: 'asc'},
                ],

                assignees: options,

                statuses: [
                    {label: 'All', value: null},
                    {label: 'Created', value: 'created'},
                    {label: 'Assigned', value: 'assigned'},
                    {
                        label: 'Awaiting external',
                        value: 'awaiting-external'
                    },
                ],

                assignment: [
                    {label: 'All', value: null},
                    {label: 'Unassigned', value: 'unassigned'},
                    {label: 'Assigned', value: 'assigned'},
                ],

                linkage: [
                    {label: 'All', value: null},
                    {label: 'Linked', value: 'linked'},
                    {label: 'Unlinked', value: 'unlinked'},
                ]
            }
        },

        async addNote(ticketId, note) {
            await api.post(`tickets/${ticketId}/add-note`, {
                note
            })

            await this.getTicket(ticketId)
        },

        async getStats(date) {
            this._stats = null

            const response = await api.get(`tickets/stats`, {
                date
            })

            this._stats = response.data
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(useTicketStore, import.meta.hot)
    )
}
